import { lockScroll, unlockScroll } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/*
 * Behaviour that belongs to the Pretty Paw cart drawer and to nothing else.
 *
 * Everything the cart actually does — quantity, removal, re-rendering, opening
 * and closing — is Horizon's, and none of it is touched here. The cross sell
 * used to step through its offers here; it is now a plain stack of the top
 * offers and needs no script at all.
 */

/*
 * The subscription offer.
 *
 * The switch is the whole control: turning it on puts the subscription in the
 * cart on whichever plan is chosen, turning it off takes it back out. There is
 * no add button in the design and none is rendered, so the switch has to carry
 * both halves.
 *
 * Adding is Horizon's own `product-form-component`, asked to submit, so the
 * cart write and the re-render that follows are not reimplemented here.
 * Removing reuses the line's own remove button for the same reason: it is the
 * path the rest of the drawer already uses and it is already tested.
 *
 * Choosing a different row while the offer is already in the cart — the plan
 * row or the Einmalkauf row, which is the same pack with no plan — changes
 * that line rather than adding a second one, which is the only part with no
 * ready-made path, so it is the only fetch in this file.
 *
 * With this module missing the offer still opens and shuts — that is a
 * checkbox and a label, driven by the cascade — and the form's own submit
 * button, revealed by a noscript rule, still adds.
 */
class PpCartSubscription extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get #toggle() {
    return this.querySelector('.pp-cart-subscription__toggle');
  }

  /** The cart line this offer is already sitting on, empty when it is not. */
  get #lineKey() {
    return this.dataset.lineKey ?? '';
  }

  /** The variant that line is sitting on, so a pack change can be told from a
   * plain plan change. */
  get #activeVariant() {
    return this.dataset.activeVariant ?? '';
  }

  /** The plan that line was bought on, empty when it carries none — which is
   * what the Einmalkauf row stands for, so empty here matches that row's
   * empty value and the no-op check below needs no special case. */
  get #activePlan() {
    return this.dataset.activePlan ?? '';
  }

  /** How many the line holds, so switching rows keeps the shopper's quantity
   * instead of clamping it back to one. */
  get #lineQuantity() {
    return Number(this.dataset.lineQuantity) || 1;
  }

  /**
   * The in-flight mark. On the element it dims and locks the rows through the
   * stylesheet — the lock is what stops a second click racing the first —
   * and on the tracked cart line it lights the same spinner a quantity
   * change lights, so the wait looks like the wait the shopper already
   * knows. A successful write morphs both marks away with the section; this
   * only has to clean up after a failure.
   *
   * @param {boolean} on
   */
  #setBusy(on) {
    this.toggleAttribute('data-busy', on);

    const key = this.#lineKey;
    if (!key) return;
    document
      .querySelector(`.pp-cart-line[data-key="${CSS.escape(key)}"]`)
      ?.classList.toggle('pp-cart-line--updating', on);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target === this.#toggle) {
      target.checked ? this.#add() : this.#remove();
      return;
    }

    // A row chosen while the offer is off only marks it. The switch is what
    // buys it, so picking a row must not write to the cart by itself.
    if (target.name === 'selling_plan' && this.#lineKey) {
      this.#changeRow(target.value, target.dataset.variantId ?? '');
    }
  };

  #add() {
    this.querySelector('form')?.requestSubmit();
  }

  #remove() {
    const key = this.#lineKey;
    if (!key) return;

    const button = document.querySelector(
      `.pp-cart-line[data-key="${CSS.escape(key)}"] .pp-cart-line__remove`
    );

    if (button instanceof HTMLElement) {
      button.click();
      return;
    }

    // The line is in the cart but not drawn — a nested line, say. Fall back to
    // the cart itself.
    this.#write({ id: key, quantity: 0 });
  }

  /**
   * A different row while the offer is in the cart.
   *
   * When the row is the same variant the cart can change the line in place, so
   * it does: onto the plan, or off it — `selling_plan: null` is how the change
   * route strips a plan from a line. When the row is a different pack it is a
   * different variant, and the cart cannot move a line to another variant —
   * `cart/change` keyed by a line only takes quantity, properties and a
   * selling plan — so the old line is removed and the new pack added instead.
   * Either way the line count stays one; a row change swaps rather than
   * stacks. The same swap is the fallback when the change route refuses the
   * null: the shopper asked to keep the pack and drop the plan, and remove
   * plus re-add answers that too, just less gently.
   *
   * An empty planId is the Einmalkauf row. It matches an empty #activePlan,
   * which is what makes the equality below a complete no-op check: clicking
   * the row the line already is must not write to the cart at all.
   *
   * @param {string} planId
   * @param {string} variantId
   */
  async #changeRow(planId, variantId) {
    if (planId === this.#activePlan && variantId === this.#activeVariant) return;

    this.#setBusy(true);
    try {
      if (variantId && variantId !== this.#activeVariant) {
        await this.#swapPack(variantId, planId);
        return;
      }

      const changed = await this.#write({
        id: this.#lineKey,
        quantity: this.#lineQuantity,
        selling_plan: planId ? Number(planId) : null,
      });

      if (!changed && !planId) {
        await this.#swapPack(variantId || this.#activeVariant, planId);
      }
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Move the offer to another line shape: drop the current line, add the
   * chosen variant — on its plan when the row carries one, plain when it is
   * the Einmalkauf — and re-render once from the add.
   *
   * @param {string} variantId
   * @param {string} planId
   */
  async #swapPack(variantId, planId) {
    const sectionId = this.dataset.sectionId;
    const key = this.#lineKey;
    if (!sectionId || !key) return;

    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

    try {
      const removed = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!removed.ok) return;

      /*
       * No `.js` appended here, unlike the change calls either side of it.
       * Horizon's route table is not consistent: `cart_change_url` is
       * `/cart/change` and takes the suffix, `cart_add_url` is already
       * `/cart/add.js`, so appending gives `/cart/add.js.js`. That answers 404
       * with an empty body, the guard below returns quietly, and the line that
       * was just removed is never put back. The shopper is left with an empty
       * cart for choosing a different pack. assets/pp-subscription-popup.js
       * carries the same note over the same call.
       */
      const item = { id: Number(variantId), quantity: this.#lineQuantity };
      if (planId) item.selling_plan = Number(planId);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: [item],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) return;

      const html = (await response.json())?.sections?.[sectionId];
      if (html) morphSection(sectionId, html, { mode: 'hydration' });
    } catch {
      // A failed swap leaves the cart as it was; the drawer still shows the
      // truth, because nothing was morphed into it.
    }
  }

  /**
   * Whether the write landed is reported back, because #changeRow has a
   * second path to try when the change route refuses to strip a plan.
   *
   * @param {Record<string, unknown>} body
   * @returns {Promise<boolean>}
   */
  async #write(body) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return false;

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: sectionId, sections_url: window.location.pathname }),
      });
      if (!response.ok) return false;

      const html = (await response.json())?.sections?.[sectionId];
      if (html) morphSection(sectionId, html, { mode: 'hydration' });
      return true;
    } catch {
      // A failed write leaves the cart as it was; the drawer still shows the
      // truth, because nothing was morphed into it.
      return false;
    }
  }
}

if (!customElements.get('pp-cart-subscription')) {
  customElements.define('pp-cart-subscription', PpCartSubscription);
}

/*
 * The one click from a plain filter line onto the plan.
 *
 * The button rewrites the line it sits on — same variant, same quantity, the
 * plan carried in its data — which is the same in-place change the offer
 * card's row switch makes. Nothing is added and nothing removed, so there is
 * no line to lose if the write fails: the drawer still shows the truth,
 * because nothing was morphed into it.
 *
 * With this module missing the button draws and does nothing, and the offer
 * card's switch still carries the same journey.
 */
class PpCartToPlan extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element && event.target.closest('button');
    if (!button || button.disabled) return;

    const sectionId = this.dataset.sectionId;
    const key = this.dataset.lineKey;
    const planId = Number(this.dataset.planId);
    if (!sectionId || !key || !planId) return;

    // The click answers instantly: spinner in the icon box, button disabled,
    // before the network is asked anything. Only a failure hands the button
    // back — success plays out the checkmark and the fade, and then the
    // re-render replaces this whole line with the plan line, which has no
    // button.
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.dataset.state = 'busy';

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: Number(this.dataset.quantity) || 1,
          selling_plan: planId,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        delete this.dataset.state;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        return;
      }

      const html = (await response.json())?.sections?.[sectionId];

      // The little ceremony before the morph: the spinner turns into a
      // check, holds a beat, and the button fades. The stylesheet honours
      // reduced motion by cutting the transitions, so the waits only cost
      // time, not correctness — and the cart is already written either way.
      this.dataset.state = 'done';
      await new Promise((resolve) => setTimeout(resolve, 450));
      this.classList.add('pp-cart-line__to-plan--leaving');
      await new Promise((resolve) => setTimeout(resolve, 250));

      if (html) morphSection(sectionId, html, { mode: 'hydration' });
    } catch {
      // The line is as it was; the button comes back usable.
      delete this.dataset.state;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('pp-cart-to-plan')) {
  customElements.define('pp-cart-to-plan', PpCartToPlan);
}

/*
 * Making the cart drawer behave the way it is drawn.
 *
 * Horizon's `theme-drawer` has two modes. Below 990 it opens the dialog with
 * `showModal()` and the page dims behind it. Past 990 it opens non-modally and
 * squeezes the page aside instead, with nothing behind the panel, because a
 * non-modal dialog has no `::backdrop`.
 *
 * The design wants the modal behaviour at every width: the page stays where it
 * is and darkens. Neither half of that is switchable from markup — the
 * breakpoint is a module constant and the squeeze is a document listener with
 * no filter — so both are corrected here, for this drawer only:
 *
 * 1. The squeeze is cancelled. The class that carries it is added by an inline
 *    listener, and removing it from here raced that listener and lost, so the
 *    stylesheet cancels its one effect instead while this drawer is open. Other
 *    drawers keep their own behaviour untouched.
 * 2. The scrim in the markup provides the dim, and clicking it closes, which is
 *    what the native backdrop would have done.
 * 3. The page is held still while the drawer is open, because a dimmed page
 *    that still scrolls reads as broken. The lock is Horizon's own helper: this
 *    theme keeps html and body at overflow hidden and scrolls an inner wrapper,
 *    so a rule on those elements would do nothing. It keys locks by owner, so
 *    ours cannot collide with the one the component takes in modal mode.
 */
class PpCartDrawerShell {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;

    document.addEventListener('theme-drawer:open', this.#onOpen);
    document.addEventListener('theme-drawer:close', this.#onClose);
    this.drawer.querySelector('.pp-cart-drawer__scrim')?.addEventListener('click', this.#onScrimClick);

    /*
     * Horizon light dismisses a drawer when a click lands outside its panel, and
     * `isClickedOutside` decides that with `!panel.contains(event.target)`.
     * Anything the cart re-renders away takes its own target out of the document
     * before the click has finished bubbling — the line's remove button most of
     * all — so by the time the panel sees the event it no longer contains the
     * thing that was clicked. Stock reads that as a click on the backdrop and
     * closes the drawer under the shopper, which is the reported fault: remove
     * one line and the cart shuts, so removing a second means opening it again.
     *
     * `theme-drawer`'s own comment calls this handler inert outside modal mode.
     * It is not. Past the modal breakpoint a click outside the panel cannot
     * reach the panel at all, so every click it does see started inside, and
     * every close it performs there is wrong.
     *
     * The guard is deliberately as narrow as the fault: only a click whose
     * target has already left the document is stopped, and such a click has
     * nothing left to do anyway. That matters because every binding in this
     * theme is delegated on `document`, so a wider stop would take the close
     * button, the quantity steppers and the cross sell arrows with it.
     *
     * This listener has to run before stock's, and does, because listeners on
     * one element fire in the order they were added: stock adds its own inside
     * `open()`, which cannot happen before a shopper acts, while this is added
     * on start.
     *
     * **Corrected:** the restore path does add it earlier, and `no-persist` does
     * not prevent that. The attribute is read on the write side only; the
     * restore script ignores it. `#undoStaleRestore` below shuts a drawer that
     * arrived that way as soon as the component is defined, so the ordering
     * above only has to hold for a drawer a shopper actually opened.
     */
    this.panel = this.drawer.querySelector('dialog');
    this.panel?.addEventListener('click', this.#onPanelClick);

    window.addEventListener('pageshow', this.#onPageShow);

    /*
     * A spinner by the price marks a line whose quantity is being written to the
     * cart. The stepper's own event fires the moment it is touched — before the
     * cart component's 300ms debounce — so the feedback is instant. The cart
     * update carries a promise that settles on success or failure, which is what
     * clears the mark (the drawer re-render also strips the class on success, but
     * the promise covers a failed change that never re-renders). Both events
     * bubble to the document, which is where the stock cart pieces read them too.
     */
    document.addEventListener('quantity-selector:update', this.#onLineUpdating);
    document.addEventListener('shopify:cart:lines-update', this.#onLinesSettled);

    this.#undoStaleRestore();
  }

  /** @param {Event} event */
  #onLineUpdating = (event) => {
    const target = event.target;
    const line = target instanceof Element ? target.closest('.pp-cart-line') : null;
    line?.classList.add('pp-cart-line--updating');
  };

  /** @param {CustomEvent} event */
  #onLinesSettled = (event) => {
    const detail = /** @type {{ promise?: Promise<unknown>, lines?: Array<{ id?: string }> }} */ (
      event.detail ?? {}
    );
    const keys = Array.isArray(detail.lines) ? detail.lines.map((line) => line?.id).filter(Boolean) : [];

    const clear = () => {
      if (keys.length === 0) {
        this.#clearUpdating();
        return;
      }
      for (const key of keys) {
        this.drawer
          ?.querySelector(`.pp-cart-line[data-key="${CSS.escape(String(key))}"]`)
          ?.classList.remove('pp-cart-line--updating');
      }
    };

    if (detail.promise && typeof detail.promise.finally === 'function') {
      detail.promise.finally(clear);
    } else {
      clear();
    }
  };

  #clearUpdating() {
    for (const line of this.drawer?.querySelectorAll('.pp-cart-line--updating') ?? []) {
      line.classList.remove('pp-cart-line--updating');
    }
  }

  /*
   * Horizon honours `no-persist` on the way out and forgets it on the way in.
   *
   * The write side in `snippets/theme-drawer.liquid` filters on the attribute,
   * so this drawer never adds itself to `theme-drawer-open`. The restore script
   * in the same file filters on nothing: it reopens whatever id it finds in that
   * key. A session that stored this drawer once, before the opt out existed,
   * therefore reopens it on every load from then on, and nothing ever stops it,
   * because the restore path sets the attribute directly and never dispatches
   * the open event that would rewrite the key.
   *
   * **This is not the back forward cache**, which `#onPageShow` already covers:
   * a reload is not a persisted restore, so that handler never sees it.
   *
   * It also explains a report that looked impossible, that F5 behaved
   * differently from the reload button. It did not. Opening the drawer by hand
   * between the two attempts fires the open event, the write side runs, this
   * drawer is filtered out of the list, the key is deleted, and the next load is
   * clean. The difference was the click in between, not the key pressed.
   *
   * Closing through the component's own method is what clears it, for the same
   * reason: it dispatches `theme-drawer:close`, the write side recomputes the
   * list without this drawer, and with nothing else open the key is removed.
   *
   * The restore script gives up below 990, which is why this was only ever seen
   * on a desktop.
   */
  #undoStaleRestore() {
    /*
     * The condition is deliberately not "was it the stored key", it is "is it
     * open before a shopper could have opened it".
     *
     * This runs synchronously while the shell is being constructed, with the
     * open listener already attached one line earlier, so a panel found open
     * here cannot have been opened by a click: our own `#onOpen` would have run
     * first. Whatever put it there did so without user intent, and Kiril's
     * standing call is that this cart never comes back open.
     *
     * Written this way because the first version keyed on `no-persist` plus the
     * stored id, and the tester then found a machine where the panel survived a
     * reload anyway while another machine behaved. Guessing at that difference
     * from here is not possible; not depending on it is.
     */
    if (!this.drawer.hasAttribute('open')) return;

    customElements.whenDefined('theme-drawer').then(() => {
      /** @type {{ close?: () => void }} */ (this.drawer).close?.();

      /*
       * `close()` unwinds the panel and stays silent. The component never
       * counted itself open, because the restore only set attributes, so no
       * `theme-drawer:close` is dispatched and the two things that listen for
       * one never run. Measured: the panel shuts, and the page is left squeezed
       * aside for a drawer that is no longer there while the key that caused all
       * of it survives. Both are cleaned up here by hand.
       */
      if (!document.querySelector('theme-drawer[open]')) {
        document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');
      }

      this.#forgetPersistedId();
    });
  }

  /**
   * Drops this drawer from Horizon's restore list.
   *
   * This is the half that stops it happening again. Shutting the panel fixes
   * the load it is on; removing the id is what makes the next load clean, and
   * the one after that.
   */
  #forgetPersistedId() {
    const key = 'theme-drawer-open';

    try {
      const ids = (sessionStorage.getItem(key) || '').split(',').filter(Boolean);
      const rest = ids.filter((id) => id !== this.drawer.id);
      if (rest.length === ids.length) return;

      if (rest.length) {
        sessionStorage.setItem(key, rest.join(','));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Private browsing refuses to write. The panel is shut either way, which
      // is the part a shopper sees; it simply gets shut again next time.
    }
  }

  /*
   * A page restored from the back forward cache comes back exactly as it was
   * left. This drawer is rendered by Liquid into every page, so its markup
   * still carries the cart it was rendered with, and a panel left open comes
   * back open. Both remaining reports are that shape: a cart that reads empty
   * after adding something and stepping back to a page visited earlier, and a
   * panel that survives one kind of reload but not another.
   *
   * Neither reproduces on `shopify theme dev`, and it cannot: that server
   * answers `cache-control: no-store`, which disqualifies a page from the back
   * forward cache outright. A published theme sends no such header, which is
   * why this only shows on the real storefront.
   *
   * So a restore is treated as what it is, a page of unknown age: the drawer is
   * shut, because Kiril's call is that this cart never comes back open, and its
   * section is fetched again, because the markup on screen predates whatever
   * the shopper did in between.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    if (this.drawer.hasAttribute('open')) {
      /** @type {{ close?: () => void }} */ (this.drawer).close?.();
    }

    this.#refresh();
  };

  /** Re-renders the drawer from the server, because only it knows the cart. */
  async #refresh() {
    const sectionId = this.drawer.querySelector('[data-section-id]')?.getAttribute('data-section-id');
    if (!sectionId) return;

    try {
      const response = await fetch(`${location.pathname}?sections=${encodeURIComponent(sectionId)}`);
      if (!response.ok) return;

      const html = (await response.json())?.[sectionId];
      if (html) morphSection(sectionId, html, { mode: 'hydration' });
    } catch {
      // The drawer keeps the markup it had. Stale is better than blank, and the
      // next cart action re-renders it anyway.
    }
  }

  /** @param {MouseEvent} event */
  #onPanelClick = (event) => {
    const target = event.target;

    if (target instanceof Element && !target.isConnected) event.stopImmediatePropagation();
  };

  #isOurs(event) {
    return event.target === this.drawer || this.drawer.contains(/** @type {Node} */ (event.target));
  }

  #onOpen = (event) => {
    if (!this.#isOurs(event)) return;

    document.documentElement.classList.add('pp-cart-drawer-open');
    lockScroll(this.drawer);
  };

  #onClose = (event) => {
    if (!this.#isOurs(event)) return;

    document.documentElement.classList.remove('pp-cart-drawer-open');
    unlockScroll(this.drawer);
  };

  #onScrimClick = () => {
    /** @type {{ close?: () => void }} */ (this.drawer).close?.();
  };
}

const startShell = () => new PpCartDrawerShell();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startShell, { once: true });
} else {
  startShell();
}

/*
 * Line entrance.
 *
 * A line added to the cart arrives through the section morph as a new node,
 * and gets a class that slides it in from the left while it fades up. Only
 * keys never seen before count as new: the morph keeps lines it can match by
 * id, but if it ever re-creates one, a line that merely changed quantity must
 * not replay the entrance. Lines present at load are known from the start, so
 * opening the drawer does not animate the whole cart.
 *
 * The exit needs no script: Horizon's component-cart-items puts `.removing`
 * on the row and waits for that class's animation before taking it out, so
 * the stylesheet alone defines it.
 */
const LINE_SELECTOR = '.pp-cart-line[data-key]';
const ENTERING_CLASS = 'pp-cart-line--entering';

function startLineEntrance() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  /** @type {Set<string>} */
  const knownKeys = new Set();
  drawer.querySelectorAll(LINE_SELECTOR).forEach((line) => {
    if (line instanceof HTMLElement && line.dataset.key) knownKeys.add(line.dataset.key);
  });

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        const lines = node.matches(LINE_SELECTOR) ? [node] : [...node.querySelectorAll(LINE_SELECTOR)];
        for (const line of lines) {
          if (!(line instanceof HTMLElement)) continue;
          const key = line.dataset.key;
          if (!key || knownKeys.has(key)) continue;

          knownKeys.add(key);
          line.classList.add(ENTERING_CLASS);
          line.addEventListener('animationend', () => line.classList.remove(ENTERING_CLASS), { once: true });
        }
      }
    }
  });

  observer.observe(drawer, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startLineEntrance, { once: true });
} else {
  startLineEntrance();
}
